import React, { useState, useEffect } from 'react';
import { getFriends } from '../services/api';

const PersonalCliqq = () => {
  const [closeFriends, setCloseFriends] = useState([]);
  const [allFriends, setAllFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchFriends();
  }, []);

  const fetchFriends = async () => {
    try {
      setLoading(true);
      const response = await getFriends();
      const friends = response.data || [];
      setAllFriends(friends);
      setCloseFriends(friends.filter(friend => friend.isCloseFriend));
    } catch (err) {
      setError('Failed to load friends');    } finally {
      setLoading(false);
    }
  };

  const toggleCloseFriend = async (friendId) => {
    try {
      // This would need a backend API endpoint to update close friend status
      // For now, we'll update locally
      setCloseFriends(prev => {
        const isCurrentlyClose = prev.some(friend => friend.id === friendId);
        if (isCurrentlyClose) {
          return prev.filter(friend => friend.id !== friendId);
        } else {
          const friendToAdd = allFriends.find(friend => friend.id === friendId);
          return [...prev, { ...friendToAdd, isCloseFriend: true }];
        }
      });
    } catch (err) {
      setError('Failed to update close friend status');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold mb-4">Personal CliQQ</h2>
        <p className="text-gray-600 mb-6">
          Manage your close friends list. These friends will have special visibility of your CliQQs.
        </p>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Close Friends Section */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <span className="w-3 h-3 bg-green-500 rounded-full mr-2"></span>
            Close Friends ({closeFriends.length})
          </h3>
          {closeFriends.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {closeFriends.map(friend => (
                <div key={friend.id} className="flex items-center p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="w-10 h-10 bg-green-500 text-white rounded-full flex items-center justify-center mr-3">
                    {friend.username?.charAt(0).toUpperCase() || 'F'}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{friend.username}</p>
                    <p className="text-sm text-gray-600">{friend.email}</p>
                  </div>
                  <button
                    onClick={() => toggleCloseFriend(friend.id)}
                    className="text-green-600 hover:text-green-800 p-1"
                    title="Remove from close friends"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No close friends selected yet</p>
          )}
        </div>

        {/* All Friends Section */}
        <div>
          <h3 className="text-lg font-semibold mb-4">All Friends ({allFriends.length})</h3>
          {allFriends.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {allFriends.map(friend => {
                const isCloseFriend = closeFriends.some(cf => cf.id === friend.id);
                return (
                  <div key={friend.id} className="flex items-center p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="w-10 h-10 bg-gray-500 text-white rounded-full flex items-center justify-center mr-3">
                      {friend.username?.charAt(0).toUpperCase() || 'F'}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{friend.username}</p>
                      <p className="text-sm text-gray-600">{friend.email}</p>
                    </div>
                    <button
                      onClick={() => toggleCloseFriend(friend.id)}
                      className={`p-2 rounded-full transition-colors ${
                        isCloseFriend 
                          ? 'text-green-600 bg-green-100 hover:bg-green-200' 
                          : 'text-gray-400 bg-gray-100 hover:bg-gray-200'
                      }`}
                      title={isCloseFriend ? 'Remove from close friends' : 'Add to close friends'}
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No friends found</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PersonalCliqq;